import axios from "axios";

import { SUPPLIERS_API } from "../config";
import { Supplier, SupplierDiscountRule } from "../types/api";

export const getSuppliers = async (
  options: {
    skip?: number;
    take?: number;
    orderBy?: { field: string; dir: string };
    /** Exclude archived suppliers (what pickers want). */
    activeOnly?: boolean;
  },
  dataCallback: (data: any) => void,
  errorCallback: (data: any) => void,
  totalRowsCallback: Function
) => {
  const skip = options?.skip || 0;
  const take = options?.take || undefined;
  const orderBy = options?.orderBy || { name: "asc" };
  const activeOnly = options?.activeOnly ? true : undefined;

  axios
    .get(SUPPLIERS_API, {
      params: {
        skip: skip,
        take: take,
        orderBy: orderBy,
        includePurchaseOrders: false,
        activeOnly,
      },
    })
    .then((r) => {
      dataCallback(r.data);

      axios.get(SUPPLIERS_API + "count", { params: { activeOnly } }).then((c) => {
        totalRowsCallback(c.data.rows);
      });
    })
    .catch((e) => {
      if (errorCallback) {
        errorCallback(e);
      }
    });
};

export const createSupplier = async (
  supplier: Supplier,
  successCallback: (data: Supplier) => void,
  errorCallback: (error: any) => void
) => {
  axios
    .post(SUPPLIERS_API + "create", supplier)
    .then((response) => {
      successCallback(response.data);
    })
    .catch((error) => {
      errorCallback(error);
    });
};

export const updateSupplier = async (
  supplier: Supplier,
  successCallback: (data: Supplier) => void,
  errorCallback: (error: any) => void
) => {
  if (!supplier.guid) {
    errorCallback(new Error("Supplier GUID is required for update"));
    return;
  }
  
  axios
    .put(SUPPLIERS_API + supplier.guid + "/update", supplier)
    .then((response) => {
      successCallback(response.data);
    })
    .catch((error) => {
      errorCallback(error);
    });
};

// Deletes a supplier. The server refuses (409) while any product or purchase
// order still refers to it; the message says which.
export const deleteSupplier = async (
  guid: string,
  success: (data: Supplier) => void,
  failure: (error: any) => void
) => {
  axios
    .delete(`${SUPPLIERS_API}${guid}/delete`)
    .then((r) => success(r.data))
    .catch((e) => failure(e));
};

// Archives a supplier: hidden from pickers, kept for order history. The server
// refuses (409) while any product still refers to it.
export const archiveSupplier = async (
  guid: string,
  success: (data: Supplier) => void,
  failure: (error: any) => void
) => {
  axios
    .put(`${SUPPLIERS_API}${guid}/archive`)
    .then((r) => success(r.data))
    .catch((e) => failure(e));
};

export const unarchiveSupplier = async (
  guid: string,
  success: (data: Supplier) => void,
  failure: (error: any) => void
) => {
  axios
    .put(`${SUPPLIERS_API}${guid}/unarchive`)
    .then((r) => success(r.data))
    .catch((e) => failure(e));
};

export const searchSuppliers = async (
  options: {
    search: string;
    skip?: number;
    take?: number;
    activeOnly?: boolean;
  },
  dataCallback: (data: Supplier[]) => void,
  totalRowsCallback: (totalRows: number) => void,
  errorCallback?: (error: any) => void
) => {
  const skip = options?.skip || 0;
  const take = options?.take || undefined;
  const searchTerm = options.search.trim();

  if (!searchTerm) {
    dataCallback([]);
    totalRowsCallback(0);
    return;
  }

  // Create where clause for searching by name or salesRep
  const where: any = {
    ...(options.activeOnly ? { archivedAt: null } : {}),
    OR: [
      {
        name: {
          contains: searchTerm,
          mode: "insensitive"
        }
      },
      {
        salesRep: {
          contains: searchTerm,
          mode: "insensitive"
        }
      }
    ]
  };

  axios
    .get(SUPPLIERS_API, {
      params: {
        skip: skip,
        take: take,
        where: JSON.stringify(where),
        includePurchaseOrders: false,
      },
    })
    .then((r) => {
      dataCallback(r.data);

      // For search results, we'll use the returned data length as total
      // since we don't have a separate count endpoint that supports where clauses
      totalRowsCallback(r.data.length);
    })
    .catch((e) => {
      if (errorCallback) {
        errorCallback(e);
      }
    });
};

// Discount Rule functions

export const getDiscountRules = async (
  supplierGuid: string,
): Promise<SupplierDiscountRule[]> => {
  const response = await axios.get(
    `${SUPPLIERS_API}${supplierGuid}/discount-rules`
  );
  return response.data;
};

export const createDiscountRule = async (
  supplierGuid: string,
  rule: { ruleType: string; configuration: Record<string, any>; active?: boolean },
): Promise<SupplierDiscountRule> => {
  const response = await axios.post(
    `${SUPPLIERS_API}${supplierGuid}/discount-rules`,
    rule,
  );
  return response.data;
};

export const deleteDiscountRule = async (
  supplierGuid: string,
  ruleGuid: string,
): Promise<void> => {
  await axios.delete(
    `${SUPPLIERS_API}${supplierGuid}/discount-rules/${ruleGuid}`
  );
};
